/** Тренд: расходы стеком (группы удержаний + себестоимость; вниз от нуля, в знаке витрины) + линия
 *  выручки — ОДНА ось ₽. В подсказке расходы — без минуса, как в сводке (`shown`, соглашение —
 *  `calc.ts`): с минусом только ряд, где компенсации перевесили расходы. Маржа (`net_margin`) —
 *  отдельным мини-графиком (своя шкала, а не вторая ось); корзина без себестоимости (раньше
 *  `cogs_from`) — без столбца маржи, в подсказке «—» и «без себестоимости (до 01.09.2026)». */
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { Grain, OzFilters } from "@/api/ozonAnalytics";
import { axisTick } from "@/lib/chart-theme";
import { useIsDark } from "@/lib/use-is-dark";
import { shown } from "./calc";
import { cogsNullTitle } from "./cogs";
import { compact, fmtDate, rub } from "./format";
import { useMp } from "./mp";
import { useMeta, useTrend } from "./queries";
import { ErrorBox, Loading, Seg } from "./shared";
import {
  COGS_SERIES,
  REST_GROUPS,
  trendPoints,
  trendSeries,
  type TrendPoint,
  type TrendSeries,
} from "./trend";

const GRAINS: { id: Grain; label: string }[] = [
  { id: "month", label: "Месяцы" },
  { id: "week", label: "Недели" },
  { id: "day", label: "Дни" },
];

export function TrendChart({
  filters,
  grain,
  onGrain,
  scope,
  title,
}: {
  filters: OzFilters;
  grain: Grain;
  onGrain: (g: Grain) => void;
  /** только факты сущности (`<dim>:<key>`, карточка); нет — весь отчёт */
  scope?: string[];
  /** нет — «Выручка и расходы по периодам» */
  title?: string;
}): React.ReactElement {
  const mp = useMp();
  const q = useTrend(mp.id, filters, grain, scope);
  // группы витрины площадки (код → название): группа из /meta без своего ряда — не ошибка
  // контракта, а просто «прочие»; справочные (`reference`, WB `spp`) не рисуются вовсе
  const metaGroups = useMeta(mp.id).data?.groups ?? null;
  const reference = useMemo(
    () => new Set((metaGroups ?? []).filter((g) => g.reference).map((g) => g.code)),
    [metaGroups],
  );
  const isDark = useIsDark();
  const series = useMemo(() => trendSeries(mp.label), [mp.label]);
  const { points, unknown } = useMemo(
    () => (q.data ? trendPoints(q.data.cur, grain, reference) : { points: [], unknown: [] }),
    [q.data, grain, reference],
  );
  const inMeta = unknown.flatMap((c) => metaGroups?.filter((g) => g.code === c) ?? []);
  const notInMeta = unknown.filter((c) => !inMeta.some((g) => g.code === c));
  const revColor = isDark ? "#e7e5e4" : "#1c1917";
  const marginColor = isDark ? "#3987e5" : "#2a78d6";
  const color = (s: TrendSeries): string => (isDark ? s.dark : s.light);
  // корзина без себестоимости: «—» у себестоимости, расходов и маржи — с причиной
  const noCogs =
    q.data?.cogs?.status === "ok"
      ? `без себестоимости (до ${fmtDate(q.data.cogs.cogs_from)})`
      : `без себестоимости (${cogsNullTitle(q.data?.cogs, mp.label)})`;

  return (
    <div className="space-y-3 rounded-md border bg-card p-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm font-medium">
          {title ?? "Выручка и расходы по периодам"}
        </div>
        <div className="ml-auto">
          <Seg label="Шаг графика" value={grain} options={GRAINS} onChange={onGrain} />
        </div>
      </div>
      {q.isLoading && <Loading text="Строю тренд…" />}
      {q.isError && <ErrorBox error={q.error} what="тренд" />}
      {notInMeta.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          В витрине группы, которых нет в контракте: {notInMeta.join(", ")} — показаны в «Прочих
          удержаниях».
        </div>
      )}
      {inMeta.length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="trend-extra-groups">
          Без своего ряда, в «Прочих удержаниях»: {inMeta.map((g) => `«${g.label}»`).join(", ")}.
        </div>
      )}
      {q.data && points.length === 0 && (
        <div className="text-sm text-muted-foreground">В периоде нет начислений.</div>
      )}
      {q.data && points.length > 0 && (
        <>
          <Legend series={series} revColor={revColor} color={color} />
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={points}
              stackOffset="sign"
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid vertical={false} strokeOpacity={0.25} />
              <XAxis dataKey="label" tick={axisTick(isDark)} tickLine={false} axisLine={false} />
              <YAxis
                tick={axisTick(isDark)}
                tickLine={false}
                axisLine={false}
                width={64}
                tickFormatter={(v: number) => compact(v)}
              />
              <ReferenceLine y={0} strokeOpacity={0.5} />
              <Tooltip
                cursor={{ fillOpacity: 0.06 }}
                content={<TrendTip series={series} noCogs={noCogs} />}
              />
              {series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId="ded"
                  fill={color(s)}
                  stroke={isDark ? "#0b0b0b" : "#ffffff"}
                  strokeWidth={1}
                  maxBarSize={44}
                  isAnimationActive={false}
                />
              ))}
              <Line
                dataKey="rev"
                name="Выручка"
                stroke={revColor}
                strokeWidth={2}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div>
            <div className="mb-1 text-xs text-muted-foreground">
              Маржа (своя шкала)
            </div>
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} strokeOpacity={0.2} />
                <XAxis dataKey="label" tick={axisTick(isDark)} tickLine={false} axisLine={false} />
                <YAxis
                  tick={axisTick(isDark)}
                  tickLine={false}
                  axisLine={false}
                  width={64}
                  tickFormatter={(v: number) => compact(v)}
                />
                <ReferenceLine y={0} strokeOpacity={0.5} />
                <Tooltip cursor={{ fillOpacity: 0.06 }} content={<MarginTip noCogs={noCogs} />} />
                <Bar
                  dataKey="margin"
                  fill={marginColor}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={44}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {grain !== "day" && (
            <p className="text-[11px] text-muted-foreground">
              Крайние {grain === "week" ? "недели" : "месяцы"} могут быть неполными — корзина
              обрезана границами периода. «Прочие удержания»: {REST_GROUPS.length} групп (эквайринг, вывоз,
              страхование, подписки, компенсации).
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Legend({
  series,
  revColor,
  color,
}: {
  series: TrendSeries[];
  revColor: string;
  color: (s: TrendSeries) => string;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-0.5 w-4" style={{ background: revColor }} />
        Выручка
      </span>
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-sm" style={{ background: color(s) }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

/** Строка подсказки с суммой, которой может не быть (без себестоимости): «—» с причиной. */
function OptLine({
  label,
  value,
  why,
  className,
}: {
  label: string;
  value: number | null;
  why: string;
  className: string;
}): React.ReactElement {
  return (
    <div className={className}>
      <span>{label}</span>
      <span title={value === null ? why : undefined}>{value === null ? "—" : rub(value)}</span>
    </div>
  );
}

/** Тултип тренда: все ряды стека — группы удержаний и себестоимость (выручка в стек не входит,
 *  `trend.ts`), итог «Расходы» и «Маржа»; без себестоимости — «—» и причина (`noCogs`). */
export function TrendTip({
  series,
  noCogs,
  active,
  payload,
}: {
  /** серии площадки (`trendSeries`) */
  series: TrendSeries[];
  /** корзина без себестоимости: «без себестоимости (до 01.09.2026)» */
  noCogs: string;
  active?: boolean;
  payload?: { payload: TrendPoint }[];
}): React.ReactElement | null {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  return (
    <div
      className="rounded-md border bg-background px-3 py-2 text-xs shadow-md"
      data-testid="trend-tip"
    >
      <div className="mb-1 font-medium">{p.label}</div>
      <div className="flex justify-between gap-4 tabular-nums">
        <span>Выручка</span>
        <span>{rub(p.rev)}</span>
      </div>
      {series.map((s) =>
        s.key === COGS_SERIES && !p.hasCogs ? (
          <OptLine
            key={s.key}
            label={s.label}
            value={null}
            why={noCogs}
            className="flex justify-between gap-4 tabular-nums text-muted-foreground"
          />
        ) : (
          <div key={s.key} className="flex justify-between gap-4 tabular-nums text-muted-foreground">
            <span>{s.label}</span>
            <span>{rub(shown(Number(p[s.key]), "expense"))}</span>
          </div>
        ),
      )}
      <OptLine
        label="Расходы"
        value={p.expenses === null ? null : shown(p.expenses, "expense")}
        why={noCogs}
        className="mt-1 flex justify-between gap-4 border-t pt-1 tabular-nums"
      />
      <OptLine
        label="Маржа"
        value={p.margin}
        why={noCogs}
        className="flex justify-between gap-4 font-medium tabular-nums"
      />
      {!p.hasCogs && (
        <div className="mt-1 text-muted-foreground" data-testid="trend-tip-no-cogs">
          {noCogs}
        </div>
      )}
    </div>
  );
}

/** Тултип мини-графика маржи: в стиле TrendTip (токены темы, а не белый тултип recharts). */
export function MarginTip({
  noCogs,
  active,
  payload,
}: {
  /** корзина без себестоимости (нет маржи): «без себестоимости (до 01.09.2026)» */
  noCogs: string;
  active?: boolean;
  payload?: { payload: TrendPoint }[];
}): React.ReactElement | null {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  return (
    <div
      className="rounded-md border bg-background px-3 py-2 text-xs text-foreground shadow-md"
      data-testid="margin-tip"
    >
      <div className="mb-1 font-medium">{p.label}</div>
      <OptLine
        label="Маржа"
        value={p.margin}
        why={noCogs}
        className="flex justify-between gap-4 font-medium tabular-nums"
      />
      {p.margin === null && <div className="mt-1 text-muted-foreground">{noCogs}</div>}
    </div>
  );
}
